"use client";

import { useState, useMemo, useRef } from "react";
import { X, Palette, Type, User, Shield, ArrowLeft, Loader2, AlertTriangle, ChevronRight, Volume2 } from "lucide-react";
import { cn } from "@/utils/cn";
import { useSettings } from "@/contexts/SettingsContext";
import { EmailAuthProvider, reauthenticateWithCredential, deleteUser, sendPasswordResetEmail } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { motion, AnimatePresence } from "framer-motion";
import { APP_VERSION } from "@/utils/constants";
import { useModalA11y } from "@/utils/useModalA11y";

// --- Sub-components ---

const ToggleSwitch = ({ label, enabled, setEnabled }) => (
  <div className="flex items-center justify-between gap-4 py-2 group">
    <span className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider font-mono group-hover:text-white transition-colors">
      {label}
    </span>
    <button
      onClick={() => setEnabled(!enabled)}
      className={cn(
        "relative inline-flex h-5 w-10 shrink-0 rounded-sm transition-colors duration-200 focus:outline-none border",
        enabled ? "bg-indigo-500 border-indigo-400" : "bg-zinc-800 border-zinc-700"
      )}
    >
      <span
        className={cn(
          "inline-block h-3.5 w-3.5 transform rounded-sm bg-white shadow transition duration-200 ease-in-out mt-[2px]",
          enabled ? "translate-x-5" : "translate-x-1"
        )}
      />
    </button>
  </div>
);

const SettingsSection = ({ title, icon: Icon, children }) => (
  <div className="mb-8 break-inside-avoid">
    <div className="flex items-center gap-3 mb-5">
      <div className="p-1.5 bg-zinc-900 border border-zinc-800 rounded">
        <Icon className="text-indigo-400" size={14} />
      </div>
      <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 font-mono italic">
        {title}
      </h3>
      <div className="h-px bg-zinc-800/50 flex-1 ml-2" />
    </div>
    <div className="space-y-2 px-1">{children}</div>
  </div>
);

// --- Main Component ---

export default function SettingsModal({ isOpen, onClose, user }) {
  const [view, setView] = useState("main");
  const { settings, setSettings } = useSettings();

  const [currentPassword, setCurrentPassword] = useState("");
  const [formState, setFormState] = useState({ loading: false, error: "", success: "" });

  const resetForm = () => {
    setCurrentPassword("");
    setFormState({ loading: false, error: "", success: "" });
  };

  const handleClose = () => {
    onClose();
  };

  const dialogRef = useRef(null);
  useModalA11y(isOpen, handleClose, dialogRef);

  const hasPasswordProvider = useMemo(() =>
    user?.providerData.some((p) => p.providerId === "password")
  , [user]);

  const handleResetPassword = async () => {
    setFormState({ loading: true, error: "", success: "" });
    try {
      await sendPasswordResetEmail(auth, user.email);
      setFormState({ loading: false, error: "", success: "RESET SIGNAL SENT. CHECK INBOX." });
    } catch (error) {
      setFormState({ 
        loading: false, 
        error: error.message.toUpperCase(), 
        success: "" 
      });
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setFormState({ loading: true, error: "", success: "" });

    try {
      if (hasPasswordProvider) {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      }
      // Firestore rules deliberately forbid deleting users/{uid} (match history,
      // leaderboards, and MMR records reference it) — anonymize instead, and do
      // it BEFORE deleteUser() since that invalidates the session this write
      // needs. Otherwise the username stays reserved forever and the account
      // keeps appearing on leaderboards despite auth being gone.
      await updateDoc(doc(db, "users", user.uid), {
        username: null,
        deleted: true,
        deletedAt: serverTimestamp(),
      });
      await deleteUser(user);
      handleClose();
    } catch (error) {
      setFormState({
        loading: false,
        error: error.code === "auth/wrong-password" ? "INVALID PASSWORD." : "RE-AUTHENTICATION REQUIRED.",
        success: "",
      });
    }
  };

  const onExitComplete = () => {
    setView("main");
    resetForm();
  };

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-zinc-950/95 backdrop-blur-md"
          />

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden relative z-10 flex flex-col max-h-[85vh] shadow-2xl shadow-black"
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header - Sticky */}
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-zinc-800 bg-zinc-900 shrink-0">
          <div className="flex items-center gap-4">
            {view !== "main" && (
              <button
                onClick={() => { setView("main"); resetForm(); }}
                className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <h2 className="text-[11px] md:text-sm font-black text-white uppercase tracking-[0.4em] font-mono italic">
              {view === "main" ? "System_Settings" : view === "changePassword" ? "Security_Auth" : "Terminal_Danger"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="p-1.5 text-zinc-600 hover:text-white transition-colors border border-transparent hover:border-zinc-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Area - No fixed height */}
        <div className="p-6 md:p-8 overflow-y-auto">
          
          {view === "main" && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 columns-1 md:columns-2 gap-8">
              <SettingsSection title="Visuals" icon={Palette}>
                <div className="flex items-center justify-between gap-4 py-1">
                  <span className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider font-mono">Theme</span>
                  <div className="flex bg-zinc-950 border border-zinc-800 p-1 rounded">
                    <button className="px-4 py-1 rounded-sm text-[9px] font-black bg-indigo-600 text-white font-mono uppercase">Dark</button>
                    <button disabled className="px-4 py-1 rounded-sm text-[9px] font-black text-zinc-700 cursor-not-allowed font-mono uppercase">Light</button>
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection title="Audio" icon={Volume2}>
                <ToggleSwitch
                  label="Sound Effects"
                  enabled={settings.sound}
                  setEnabled={(val) => setSettings((s) => ({ ...s, sound: val }))}
                />
              </SettingsSection>

              <SettingsSection title="Input" icon={Type}>
                <ToggleSwitch
                  label="Smooth Caret"
                  enabled={settings.smoothCaret}
                  setEnabled={(val) => setSettings((s) => ({ ...s, smoothCaret: val }))}
                />
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-2">
                  <span className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider font-mono">Caret Style</span>
                  <div className="flex gap-1 bg-zinc-950 border border-zinc-800 p-1 rounded">
                    {["bar", "block", "underline"].map((style) => (
                      <button
                        key={style}
                        onClick={() => setSettings((s) => ({ ...s, caretStyle: style }))}
                        className={cn(
                          "px-3 py-1 rounded-sm text-[9px] font-black uppercase transition-all font-mono",
                          settings.caretStyle === style ? "bg-zinc-800 text-indigo-400 border border-zinc-700" : "text-zinc-600 hover:text-zinc-400"
                        )}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>
              </SettingsSection>

              <SettingsSection title="Identity" icon={User}>
                <button
                  onClick={() => setView("changePassword")}
                  disabled={!hasPasswordProvider}
                  className="group w-full flex items-center justify-between bg-zinc-950/50 hover:bg-indigo-500/5 border border-zinc-800 px-5 py-4 rounded transition-all disabled:opacity-20"
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="text-zinc-200 font-black text-[11px] uppercase tracking-widest font-mono">Access Keys</span>
                    <span className="text-[9px] text-zinc-500 font-mono mt-1">Modify account password</span>
                  </div>
                  <ChevronRight size={16} className="text-zinc-700 group-hover:text-indigo-400 transition-all" />
                </button>
              </SettingsSection>

              <SettingsSection title="Warning" icon={Shield}>
                <button
                  onClick={() => setView("deleteAccount")}
                  className="w-full py-4 rounded border border-red-900/30 text-red-500 hover:bg-red-600 hover:text-white font-black text-[10px] uppercase tracking-[0.3em] transition-all font-mono italic"
                >
                  Delete Account
                </button>
              </SettingsSection>
            </div>
          )}

          {view === "changePassword" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300 text-center max-w-md mx-auto">
              <div className="space-y-1 mb-8">
                <h3 className="text-white font-black text-xl uppercase italic tracking-tighter font-mono leading-none">Modify_Auth</h3>
                <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Secure Protocol Initiated</p>
              </div>
              
              {formState.error && <div className="p-3 bg-red-500/5 border-l-2 border-red-500 text-red-500 text-[10px] font-mono font-bold uppercase">{formState.error}</div>}
              {formState.success && <div className="p-3 bg-emerald-500/5 border-l-2 border-emerald-500 text-emerald-500 text-[10px] font-mono font-bold uppercase">{formState.success}</div>}

              <p className="text-zinc-400 text-xs font-mono leading-relaxed">
                For security purposes, password modifications are handled via a secure email link sent to your registered address.
                <br/><br/>
                Please confirm to dispatch the reset signal.
              </p>
              
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={formState.loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[11px] uppercase tracking-[0.2em] py-5 rounded-sm transition-all flex items-center justify-center disabled:opacity-50 mt-6 font-mono italic"
              >
                {formState.loading ? <Loader2 size={18} className="animate-spin" /> : "SEND_RESET_LINK"}
              </button>
            </div>
          )}

          {view === "deleteAccount" && (
             <form onSubmit={handleDeleteAccount} className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-500 max-w-md mx-auto">
              <div className="flex gap-5 items-start">
                <div className="shrink-0 p-4 rounded bg-red-500/10 border border-red-500/20">
                  <AlertTriangle size={32} className="text-red-500" />
                </div>
                <div className="space-y-1 text-left">
                    <h3 className="text-xl font-black text-white uppercase italic font-mono tracking-tighter">Account_Purge</h3>
                    <p className="text-zinc-500 text-[10px] font-mono leading-relaxed uppercase tracking-widest">
                        This action is irreversible. Login access will be revoked and your username released. Match history and leaderboard records are retained anonymized.
                    </p>
                </div>
              </div>

              <div className="space-y-6">
                {formState.error && (
                  <div className="p-3 bg-red-500/5 border-l-2 border-red-500 text-red-500 text-[10px] font-mono font-bold uppercase">{formState.error}</div>
                )}

                {hasPasswordProvider ? (
                  <div className="space-y-3">
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 font-mono">Verify_Authorization</label>
                    <input
                      type="password"
                      placeholder="PASSWORD_ENTRY"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="w-full p-5 bg-zinc-950 border border-zinc-800 focus:border-red-500/50 outline-none rounded-sm text-white transition-all text-center tracking-[0.5em] font-mono"
                    />
                  </div>
                ) : (
                  <div className="p-5 rounded border border-amber-900/20 bg-amber-950/5">
                    <p className="text-[10px] text-amber-500 font-mono leading-snug uppercase tracking-wider">
                      <strong>OAuth_Detected:</strong> Verification may require a re-authentication handshake with your provider.
                    </p>
                  </div>
                )}
                
                <div className="flex flex-col gap-4">
                  <button
                    type="submit"
                    disabled={formState.loading}
                    className="w-full bg-red-600 hover:bg-red-500 text-white font-black text-[11px] uppercase tracking-[0.3em] py-5 rounded-sm transition-all flex items-center justify-center font-mono italic"
                  >
                    {formState.loading ? <Loader2 size={18} className="animate-spin" /> : "EXECUTE_PURGE"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setView("main"); resetForm(); }}
                    className="text-zinc-600 hover:text-white text-[10px] font-black uppercase tracking-widest transition-colors font-mono"
                  >
                    ABORT_PROCESS
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
        
        {/* Footer - Sticky */}
        <div className="p-4 bg-zinc-900 border-t border-zinc-800 flex justify-between items-center px-6 shrink-0">
            <span className="text-[8px] font-mono text-zinc-400 uppercase tracking-widest">{APP_VERSION}</span>
            <div className="flex gap-2">
                <div className="w-1 h-1 bg-zinc-800 rounded-full" />
                <div className="w-1 h-1 bg-zinc-800 rounded-full" />
                <div className="w-1 h-1 bg-indigo-500 rounded-full animate-pulse" />
            </div>
        </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}